import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type { Audience } from '@dmn/shared';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ReportingView } from '@/components/ReportingView';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { useSplitReporting } from '@/lib/useSplitReporting';
import { daysAgoUtc, formatCount, todayUtc } from '@/lib/utils';

export const Route = createFileRoute('/reporting/')({ component: Reporting });

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const;

const ALL_CAMPAIGNS = 'all';

/**
 * Patient and HCP delivery are reported separately.
 *
 * Not a filter, because the two are not the same question narrowed — they have
 * different metrics. An HCP surface is not consent-gated at all, so its consent
 * counters are structurally zero rather than merely empty, and `ReportingView`
 * drops them for that audience. A dropdown would imply the shape of the answer
 * stays the same; tabs say plainly that it does not.
 *
 * Plain-English first with the acronym after: the same component renders in the
 * sponsor portal, where "DTC" is internal vocabulary.
 */
const TABS = [
  { audience: 'patient' as const, label: 'Patients', short: 'DTC' },
  { audience: 'hcp' as const, label: 'Practices', short: 'HCP' },
];

function Reporting() {
  const queryClient = useQueryClient();
  const [days, setDays] = useState('30');
  const [audience, setAudience] = useState<Audience>('patient');
  const [campaignId, setCampaignId] = useState<string>(ALL_CAMPAIGNS);

  /**
   * `null` until Remote Config has been read. The page renders its skeleton
   * until then rather than picking a layout it may have to swap out.
   */
  const split = useSplitReporting();

  const from = daysAgoUtc(Number(days) - 1);
  const to = todayUtc();

  const campaigns = useQuery({ queryKey: queryKeys.campaigns, queryFn: api.campaigns.list });
  const placements = useQuery({ queryKey: queryKeys.placements, queryFn: api.placements.list });

  /** Placement id → audience, so the campaign list can be scoped to the tab. */
  const audienceByPlacement = useMemo(
    () => new Map((placements.data ?? []).map((placement) => [placement.id, placement.audience])),
    [placements.data]
  );

  /**
   * Campaigns that can deliver on the active tab.
   *
   * Without this the dropdown offers HCP campaigns while the DTC tab is open,
   * and picking one yields an empty page that looks like a bug rather than a
   * category error. A campaign counts for an audience if any of its placements
   * belongs to it — today none span both, but nothing forbids it.
   */
  const tabCampaigns = useMemo(
    () =>
      // Unsplit, there is no tab to scope to and every campaign belongs.
      split === true
        ? (campaigns.data ?? []).filter((campaign) =>
            campaign.placementIds.some((id) => audienceByPlacement.get(id) === audience)
          )
        : (campaigns.data ?? []),
    [campaigns.data, audienceByPlacement, audience, split]
  );

  const selectable = tabCampaigns.some((campaign) => campaign.id === campaignId);
  const scoped = campaignId === ALL_CAMPAIGNS || !selectable ? undefined : campaignId;

  // Both audiences are fetched regardless of which tab is open, so the tab
  // labels can carry their own totals and switching costs nothing. `enabled`
  // keeps the unused half off the wire while the flag is off or still unknown —
  // the hooks still run, because they cannot be called conditionally.
  const patient = useQuery({
    queryKey: queryKeys.reporting(from, to, audience === 'patient' ? scoped : undefined, 'patient'),
    queryFn: () =>
      api.reporting.get(from, to, audience === 'patient' ? scoped : undefined, 'patient'),
    enabled: split === true,
  });
  const hcp = useQuery({
    queryKey: queryKeys.reporting(from, to, audience === 'hcp' ? scoped : undefined, 'hcp'),
    queryFn: () => api.reporting.get(from, to, audience === 'hcp' ? scoped : undefined, 'hcp'),
    enabled: split === true,
  });

  /** The pre-tabs view: both audiences in one table, consent columns included. */
  const blended = useQuery({
    queryKey: queryKeys.reporting(from, to, scoped, 'all'),
    queryFn: () => api.reporting.get(from, to, scoped), // no audience param: the server reports both
    enabled: split === false,
  });

  const byAudience = { patient, hcp } as const;

  const aggregate = useMutation({
    mutationFn: api.reporting.aggregate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reporting'] }),
  });

  return (
    <>
      <PageHeader
        title="Reporting"
        description="Aggregated from rollups — never from raw per-person events"
        actions={
          <>
            <Select
              value={selectable ? campaignId : ALL_CAMPAIGNS}
              onValueChange={setCampaignId}
              disabled={tabCampaigns.length === 0}
            >
              <SelectTrigger className="h-8 w-[210px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CAMPAIGNS}>
                  {split === true
                    ? `All ${TABS.find((tab) => tab.audience === audience)?.short} campaigns`
                    : 'All campaigns'}
                </SelectItem>
                {tabCampaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="h-8 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              disabled={aggregate.isPending}
              onClick={() => aggregate.mutate()}
              title="Rollups run every 15 minutes; this catches up immediately."
            >
              <RefreshCw className={aggregate.isPending ? 'size-4 animate-spin' : 'size-4'} />
              Refresh rollups
            </Button>
          </>
        }
      />
      <PageBody>
        {/*
          Flag off — or not yet known — is the single blended table this page
          showed before the split. `audience="all"` keeps the consent columns,
          which is correct here: the numbers include patient delivery.
        */}
        {split !== true && (
          <ReportingView
            summary={blended.data}
            isPending={split === null || blended.isPending}
            isError={blended.isError}
            error={blended.error}
            onRetry={() => void blended.refetch()}
            audience="all"
          />
        )}

        {split === true && (
          <Tabs value={audience} onValueChange={(next) => setAudience(next as Audience)}>
            <TabsList>
              {TABS.map((tab) => {
                const query = byAudience[tab.audience];
                return (
                  <TabsTrigger key={tab.audience} value={tab.audience}>
                    {tab.label} ({tab.short})
                    {/*
                    The impression count on the label answers "is there anything
                    in here" before the click. Both tabs always render, even at
                    zero: hiding one would make the page's shape depend on the
                    date range.
                  */}
                    {!query.isPending && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {formatCount(query.data?.totals.impressions ?? 0)}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {TABS.map((tab) => {
              const query = byAudience[tab.audience];
              return (
                <TabsContent key={tab.audience} value={tab.audience} className="mt-4">
                  <ReportingView
                    summary={query.data}
                    isPending={query.isPending}
                    isError={query.isError}
                    error={query.error}
                    onRetry={() => void query.refetch()}
                    audience={tab.audience}
                  />
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </PageBody>
    </>
  );
}
